import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Calculator, Loader2, ShieldCheck } from 'lucide-react';
import type {
  DataCenterOperatingModel,
  DataCenterWorkload,
  EconomicContributionSiteInputs,
  EconomicScreeningScenarioOption,
  EconomicScreeningSiteDefaults,
  PowerMwBasis,
  WaterUseInputContext,
} from '../../../types/economicContribution';
import { ECONOMIC_CONTRIBUTION_SCHEMA_VERSION } from '../../../types/economicContribution';
import type { BopHeatRejection, ProjectIntent } from '../../../types/siteRag';
import { HEAT_REJECTION_LABELS } from '../../../lib/reportPreferences/labels';
import { Button } from '../../ui/button';

interface EconomicScreeningControlsProps {
  inputs: EconomicContributionSiteInputs;
  defaultLoadMw: number | null;
  defaultLoadRange: { low: number; high: number } | null;
  defaultLoadBasis: PowerMwBasis;
  /** Canonical Intent/financial/profile defaults. Untouched values remain non-sticky. */
  defaults?: EconomicScreeningSiteDefaults;
  lastGeneratedAt?: string;
  onRun?: (inputs: EconomicContributionSiteInputs) => Promise<void>;
}

export interface EconomicScreeningControlsHandle {
  openFiscalScenario: () => void;
}

const SITE_DEFAULT = '__site-default__' as const;
type Defaultable<T extends string> = T | typeof SITE_DEFAULT;

interface FormDraft {
  phaseBasis: string;
  loadMw: string;
  loadSource: 'scenario' | 'stored' | 'edited';
  loadBasis: Defaultable<PowerMwBasis>;
  operatingModel: Defaultable<DataCenterOperatingModel>;
  workload: Defaultable<DataCenterWorkload>;
  duration: string;
  durationSource: 'scenario' | 'stored' | 'edited';
  utilizationLowPct: string;
  utilizationHighPct: string;
  utilizationSource: 'scenario' | 'stored' | 'edited';
  heatRejection: Defaultable<BopHeatRejection>;
  rackCoolingStrategy: Defaultable<ProjectIntent['coolingStrategy']>;
  fiscalEnabled: boolean;
  jurisdiction: string;
  assessedValue: string;
  taxRatePct: string;
  fiscalYears: string;
  abatementPct: string;
  annualPilot: string;
  pilotTreatment: 'replacement' | 'additional';
}

function optionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function displayLabel(value: string): string {
  return value
    .split('-')
    .map(part => part === 'hpc' ? 'HPC' : part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function fallbackDefaults(
  defaultLoadMw: number | null,
  defaultLoadRange: { low: number; high: number } | null,
  defaultLoadBasis: PowerMwBasis,
): EconomicScreeningSiteDefaults {
  const whole: EconomicScreeningScenarioOption = {
    id: 'declared-project',
    label: defaultLoadMw != null
      ? `Whole project — ${defaultLoadMw.toLocaleString('en-US')} MW`
      : defaultLoadRange
        ? `Whole project — ${defaultLoadRange.low.toLocaleString('en-US')}–${defaultLoadRange.high.toLocaleString('en-US')} MW`
        : 'Whole project — MW not yet bounded',
    phaseBasis: 'declared-project',
    ...(defaultLoadMw != null ? { loadMw: defaultLoadMw } : {}),
    ...(defaultLoadRange ? { loadRange: defaultLoadRange } : {}),
    loadBasis: defaultLoadBasis,
    operatingModel: 'unknown',
    workload: 'unknown',
    ...(!defaultLoadMw && !defaultLoadRange ? { disabledReason: 'Record an exact or bounded project MW value.' } : {}),
  };
  return {
    ...(defaultLoadMw != null ? { loadMw: defaultLoadMw } : {}),
    ...(defaultLoadRange ? { loadRange: defaultLoadRange } : {}),
    loadBasis: defaultLoadBasis,
    phaseBasis: 'declared-project',
    operatingModel: 'unknown',
    workload: 'unknown',
    scenarioOptions: [whole],
  };
}

function scenarioFor(
  defaults: EconomicScreeningSiteDefaults,
  phaseBasis: string,
): EconomicScreeningScenarioOption {
  return defaults.scenarioOptions.find(option => option.phaseBasis === phaseBasis)
    ?? defaults.scenarioOptions[0]!;
}

function defaultMode<T extends string>(explicit: T | undefined): Defaultable<T> {
  // `unknown` / `undecided` are deliberate, persistable overrides. Only an
  // absent field means "follow the current Intent-derived site default".
  return explicit == null ? SITE_DEFAULT : explicit;
}

function buildDraft(
  inputs: EconomicContributionSiteInputs,
  defaults: EconomicScreeningSiteDefaults,
): FormDraft {
  const phaseBasis = inputs.phaseBasis?.trim() || defaults.phaseBasis;
  const scenario = scenarioFor(defaults, phaseBasis);
  const existingFiscal = inputs.fiscalScenarios?.[0];
  const storedLoad = inputs.loadMw;
  const storedDuration = inputs.constructionDurationMonths;
  const storedUtilization = inputs.waterUse?.utilization;
  const scenarioUtilization = scenario.waterUse?.utilization;
  const utilization = storedUtilization ?? scenarioUtilization;
  const storedHeat = inputs.waterUse?.heatRejection;
  const storedRack = inputs.waterUse?.rackCoolingStrategy;
  return {
    phaseBasis,
    // A persisted range is an explicit refinement. Do not paint the current
    // Intent exact value into the field while retaining that older range in
    // the submitted payload: the user must see the quantity that will run.
    loadMw: String(storedLoad ?? (inputs.loadRange == null ? scenario.loadMw ?? '' : '')),
    loadSource: storedLoad != null || inputs.loadRange != null ? 'stored' : 'scenario',
    loadBasis: inputs.loadBasis && inputs.loadBasis !== scenario.loadBasis
      ? inputs.loadBasis
      : SITE_DEFAULT,
    operatingModel: defaultMode(inputs.operatingModel),
    workload: defaultMode(inputs.workload),
    duration: String(storedDuration ?? scenario.constructionDurationMonths ?? ''),
    durationSource: storedDuration != null ? 'stored' : 'scenario',
    utilizationLowPct: utilization ? String(utilization.low * 100) : '',
    utilizationHighPct: utilization ? String(utilization.high * 100) : '',
    utilizationSource: storedUtilization ? 'stored' : 'scenario',
    heatRejection: defaultMode(storedHeat),
    rackCoolingStrategy: defaultMode(storedRack),
    fiscalEnabled: existingFiscal != null,
    jurisdiction: existingFiscal?.jurisdictionLabel ?? defaults.jurisdictionLabel ?? 'Local jurisdiction',
    assessedValue: String(existingFiscal?.assessedValueUsd ?? ''),
    taxRatePct: existingFiscal ? String(existingFiscal.annualTaxRateDecimal * 100) : '',
    fiscalYears: String(existingFiscal?.years ?? 10),
    abatementPct: String(existingFiscal?.abatementPctByYear?.[0] ?? ''),
    annualPilot: String(existingFiscal?.pilotUsdByYear?.[0] ?? ''),
    pilotTreatment: existingFiscal?.pilotTreatment ?? 'replacement',
  };
}

function withoutEmptyWaterContext(context: WaterUseInputContext): WaterUseInputContext | undefined {
  return Object.keys(context).length > 0 ? context : undefined;
}

export const EconomicScreeningControls = React.forwardRef<
  EconomicScreeningControlsHandle,
  EconomicScreeningControlsProps
>(({
    inputs,
    defaultLoadMw,
    defaultLoadRange,
    defaultLoadBasis,
    defaults,
    lastGeneratedAt,
    onRun,
  }, ref) => {
  const effectiveDefaults = defaults ?? fallbackDefaults(defaultLoadMw, defaultLoadRange, defaultLoadBasis);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const jurisdictionInputRef = useRef<HTMLInputElement>(null);
  const seedRef = useRef({ inputs, defaults: effectiveDefaults });
  seedRef.current = { inputs, defaults: effectiveDefaults };
  const seedFingerprint = JSON.stringify(seedRef.current);
  const appliedSeedRef = useRef(seedFingerprint);
  const [draft, setDraft] = useState<FormDraft>(() => buildDraft(inputs, effectiveDefaults));
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Equivalent objects produced by unrelated Findings/profile rerenders must
  // not wipe an in-progress form. Reset only when the serialized saved inputs
  // or effective defaults materially change.
  useEffect(() => {
    if (appliedSeedRef.current === seedFingerprint) return;
    appliedSeedRef.current = seedFingerprint;
    setDraft(buildDraft(seedRef.current.inputs, seedRef.current.defaults));
  }, [seedFingerprint]);

  useImperativeHandle(ref, () => ({
    openFiscalScenario: () => {
      setDraft(current => ({ ...current, fiscalEnabled: true }));
      if (detailsRef.current) detailsRef.current.open = true;
      window.setTimeout(() => {
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        detailsRef.current?.scrollIntoView?.({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
        jurisdictionInputRef.current?.focus();
      }, 0);
    },
  }), []);

  if (!onRun) return null;

  const activeScenario = scenarioFor(effectiveDefaults, draft.phaseBasis);
  const effectiveLoadRange = draft.loadSource === 'stored'
    ? inputs.loadRange ?? activeScenario.loadRange
    : activeScenario.loadRange;
  const effectiveLoadBasis = draft.loadBasis === SITE_DEFAULT
    ? activeScenario.loadBasis
    : draft.loadBasis;
  const effectiveOperatingModel = draft.operatingModel === SITE_DEFAULT
    ? activeScenario.operatingModel
    : draft.operatingModel;
  const effectiveWorkload = draft.workload === SITE_DEFAULT
    ? activeScenario.workload
    : draft.workload;
  const effectiveHeatRejection = draft.heatRejection === SITE_DEFAULT
    ? activeScenario.waterUse?.heatRejection ?? 'unknown'
    : draft.heatRejection;
  const effectiveRackCooling = draft.rackCoolingStrategy === SITE_DEFAULT
    ? activeScenario.waterUse?.rackCoolingStrategy ?? 'undecided'
    : draft.rackCoolingStrategy;
  const selectedPue = inputs.waterUse?.pue ?? activeScenario.waterUse?.pue;

  const run = async (): Promise<void> => {
    const parsedLoad = optionalNumber(draft.loadMw);
    if (draft.loadMw.trim() && (parsedLoad == null || parsedLoad <= 0)) {
      setError('Project load must be a positive number.');
      return;
    }
    const scenarioHasLoad = parsedLoad != null
      || activeScenario.loadMw != null
      || effectiveLoadRange != null;
    if (!scenarioHasLoad) {
      setError('Record a bounded project MW range or an exact positive MW value.');
      return;
    }
    const parsedDuration = optionalNumber(draft.duration);
    if (draft.duration.trim() && (parsedDuration == null || parsedDuration <= 0)) {
      setError('Construction duration must be positive.');
      return;
    }
    const parsedUtilizationLowPct = optionalNumber(draft.utilizationLowPct);
    const parsedUtilizationHighPct = optionalNumber(draft.utilizationHighPct);
    const hasUtilizationEntry = Boolean(
      draft.utilizationLowPct.trim() || draft.utilizationHighPct.trim(),
    );
    if (hasUtilizationEntry && (
      parsedUtilizationLowPct == null
      || parsedUtilizationHighPct == null
      || parsedUtilizationLowPct <= 0
      || parsedUtilizationHighPct > 100
      || parsedUtilizationHighPct < parsedUtilizationLowPct
    )) {
      setError('Annual utilization must include a low and high value above 0%, no more than 100%, with high at least low.');
      return;
    }
    const parsedAssessedValue = optionalNumber(draft.assessedValue);
    const parsedTaxRatePct = optionalNumber(draft.taxRatePct);
    const parsedFiscalYears = optionalNumber(draft.fiscalYears);
    const parsedAbatement = optionalNumber(draft.abatementPct) ?? 0;
    const parsedPilot = optionalNumber(draft.annualPilot) ?? 0;
    if (draft.fiscalEnabled && (
      parsedAssessedValue == null || parsedAssessedValue < 0
      || parsedTaxRatePct == null || parsedTaxRatePct <= 0 || parsedTaxRatePct > 100
      || parsedFiscalYears == null || !Number.isInteger(parsedFiscalYears) || parsedFiscalYears < 1 || parsedFiscalYears > 100
      || parsedAbatement < 0 || parsedAbatement > 100 || parsedPilot < 0
      || !draft.jurisdiction.trim()
    )) {
      setError('Complete the fiscal scenario with a jurisdiction, assessed value, rate, 1–100 years, and valid abatement/PILOT values.');
      return;
    }

    const next: EconomicContributionSiteInputs = { ...inputs };
    if (draft.phaseBasis === effectiveDefaults.phaseBasis) delete next.phaseBasis;
    else next.phaseBasis = draft.phaseBasis;

    if (draft.loadSource === 'edited' && parsedLoad != null) {
      next.loadMw = parsedLoad;
      delete next.loadRange;
    } else if (draft.loadSource === 'scenario' || draft.loadSource === 'edited') {
      delete next.loadMw;
      delete next.loadRange;
    }
    if (draft.loadBasis === SITE_DEFAULT) delete next.loadBasis;
    else next.loadBasis = effectiveLoadBasis;
    if (draft.operatingModel === SITE_DEFAULT) delete next.operatingModel;
    else next.operatingModel = effectiveOperatingModel;
    if (draft.workload === SITE_DEFAULT) delete next.workload;
    else next.workload = effectiveWorkload;
    if (draft.durationSource === 'edited' && parsedDuration != null) {
      next.constructionDurationMonths = parsedDuration;
    } else if (draft.durationSource === 'scenario' || draft.durationSource === 'edited') {
      delete next.constructionDurationMonths;
    }

    const nextWater: WaterUseInputContext = { ...(inputs.waterUse ?? {}) };
    // Old drafts could duplicate an unknown workload inside water refinements.
    // Let the canonical Intent/default workload speak instead.
    if (nextWater.workload === 'unknown') delete nextWater.workload;
    if (draft.utilizationSource === 'edited' && hasUtilizationEntry) {
      nextWater.utilization = {
        low: parsedUtilizationLowPct! / 100,
        high: parsedUtilizationHighPct! / 100,
        source: 'site-record',
        sourceLabel: 'User-recorded annual utilization screening range',
      };
    } else if (draft.utilizationSource === 'scenario' || draft.utilizationSource === 'edited') {
      delete nextWater.utilization;
    }
    if (draft.heatRejection === SITE_DEFAULT) delete nextWater.heatRejection;
    else nextWater.heatRejection = effectiveHeatRejection;
    if (draft.rackCoolingStrategy === SITE_DEFAULT) delete nextWater.rackCoolingStrategy;
    else nextWater.rackCoolingStrategy = effectiveRackCooling;
    const normalizedWater = withoutEmptyWaterContext(nextWater);
    if (normalizedWater) next.waterUse = normalizedWater;
    else delete next.waterUse;

    const startYear = new Date().getFullYear();
    next.fiscalScenarios = draft.fiscalEnabled ? [{
      schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
      scenarioId: 'manual-local-scenario-1',
      label: 'Illustrative manual fiscal scenario',
      jurisdictionLabel: draft.jurisdiction.trim(),
      currency: 'USD',
      currencyYear: startYear,
      startYear,
      years: parsedFiscalYears!,
      assessedValueUsd: parsedAssessedValue!,
      annualTaxRateDecimal: parsedTaxRatePct! / 100,
      abatementPctByYear: Array(parsedFiscalYears!).fill(parsedAbatement),
      ...(parsedPilot > 0 ? { pilotUsdByYear: Array(parsedFiscalYears!).fill(parsedPilot) } : {}),
      pilotTreatment: draft.pilotTreatment,
    }] : [];

    setError(null);
    setRunning(true);
    try {
      await onRun(next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Economic screen failed.');
    } finally {
      setRunning(false);
    }
  };

  const savedCustomPhase = draft.phaseBasis !== effectiveDefaults.phaseBasis
    && !effectiveDefaults.scenarioOptions.some(option => option.phaseBasis === draft.phaseBasis)
    ? draft.phaseBasis
    : null;

  return (
    <details
      ref={detailsRef}
      id="economic-screening-controls"
      className="mt-4 scroll-mt-4 border border-border-subtle bg-state-hover/30"
      data-testid="economic-screening-controls"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-xs font-semibold text-text-primary">
        <span className="flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" aria-hidden />Adjust screening inputs</span>
        <span className="type-caption font-normal uppercase tracking-[0.09em] text-text-muted">
          {lastGeneratedAt ? `Updated ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(lastGeneratedAt))}` : 'Using Intent defaults'}
        </span>
      </summary>
      <div className="grid gap-3 border-t border-border-subtle px-3 py-3 sm:grid-cols-2 xl:grid-cols-3">
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          Screening scenario
          <select
            aria-label="Economic screening scenario"
            value={draft.phaseBasis}
            onChange={(event) => {
              const phaseBasis = event.target.value;
              const scenario = scenarioFor(effectiveDefaults, phaseBasis);
              setDraft(current => ({
                ...current,
                phaseBasis,
                loadMw: String(scenario.loadMw ?? ''),
                loadSource: 'scenario',
                loadBasis: SITE_DEFAULT,
                duration: String(scenario.constructionDurationMonths ?? ''),
                durationSource: 'scenario',
                utilizationLowPct: scenario.waterUse?.utilization
                  ? String(scenario.waterUse.utilization.low * 100)
                  : '',
                utilizationHighPct: scenario.waterUse?.utilization
                  ? String(scenario.waterUse.utilization.high * 100)
                  : '',
                utilizationSource: 'scenario',
              }));
            }}
            className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary"
          >
            {savedCustomPhase ? <option value={savedCustomPhase}>Saved basis — {savedCustomPhase}</option> : null}
            {effectiveDefaults.scenarioOptions.map(option => (
              <option key={option.id} value={option.phaseBasis} disabled={Boolean(option.disabledReason)}>
                {option.label}{option.disabledReason ? ' — unavailable' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          Project load refinement (optional)
          <div className="mt-1 flex">
            <input
              type="number"
              min="0.1"
              step="0.1"
              aria-label="Economic project load MW"
              value={draft.loadMw}
              onChange={(event) => setDraft(current => ({ ...current, loadMw: event.target.value, loadSource: 'edited' }))}
              placeholder={effectiveLoadRange ? `Using ${effectiveLoadRange.low}–${effectiveLoadRange.high}` : 'Using Intent MW'}
              className="min-w-0 flex-1 border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary outline-none focus:border-primary"
            />
            <span className="border-y border-r border-border-subtle px-2 py-1.5 text-xs text-text-muted">MW</span>
          </div>
          {effectiveLoadRange && !draft.loadMw ? <span className="mt-1 block normal-case tracking-normal type-caption font-normal text-text-muted">The recorded {effectiveLoadRange.low}–{effectiveLoadRange.high} MW range will be screened at both endpoints.</span> : null}
        </label>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          MW basis
          <select aria-label="Economic MW basis" value={draft.loadBasis} onChange={(event) => setDraft(current => ({ ...current, loadBasis: event.target.value as FormDraft['loadBasis'] }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary">
            <option value={SITE_DEFAULT}>
              {activeScenario.loadBasisSource === 'screening-assumption' ? 'Screening default' : 'From Intent'} — {displayLabel(activeScenario.loadBasis)}
            </option>
            <option value="it">IT load</option><option value="facility">Facility demand</option><option value="utility-service">Utility-service capacity</option>
          </select>
          {activeScenario.loadBasisSource === 'screening-assumption' && draft.loadBasis === SITE_DEFAULT ? (
            <span className="mt-1 block normal-case tracking-normal type-caption font-normal text-amber-700 dark:text-amber-300">The project MW basis is not recorded. Facility demand is used only as a visible screening default; choose the correct basis here or in Intent.</span>
          ) : effectiveLoadBasis === 'facility' ? (
            <span className="mt-1 block normal-case tracking-normal type-caption font-normal text-text-muted">Staffing uses a disclosed facility-to-utility-service screening proxy; water keeps the facility basis.</span>
          ) : effectiveLoadBasis === 'it' && selectedPue ? (
            <span className="mt-1 block normal-case tracking-normal type-caption font-normal text-text-muted">Staffing converts IT load with the shown PUE, then applies the disclosed utility-service proxy. Water keeps the IT basis.</span>
          ) : effectiveLoadBasis === 'it' ? (
            <span className="mt-1 block normal-case tracking-normal type-caption font-normal text-text-muted">IT load stays on its recorded basis. Staffing remains unavailable until a usable PUE is recorded or configuration-derived.</span>
          ) : null}
        </label>
        <fieldset className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          <legend>Annual utilization range (optional)</legend>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <label className="font-normal normal-case tracking-normal">
              Low %
              <input
                type="number"
                min="0.1"
                max="100"
                step="0.1"
                aria-label="Annual utilization low percent"
                value={draft.utilizationLowPct}
                onChange={(event) => setDraft(current => ({
                  ...current,
                  utilizationLowPct: event.target.value,
                  utilizationSource: 'edited',
                }))}
                placeholder="50"
                className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary outline-none focus:border-primary"
              />
            </label>
            <label className="font-normal normal-case tracking-normal">
              High %
              <input
                type="number"
                min="0.1"
                max="100"
                step="0.1"
                aria-label="Annual utilization high percent"
                value={draft.utilizationHighPct}
                onChange={(event) => setDraft(current => ({
                  ...current,
                  utilizationHighPct: event.target.value,
                  utilizationSource: 'edited',
                }))}
                placeholder="100"
                className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary outline-none focus:border-primary"
              />
            </label>
          </div>
          <span className="mt-1 block type-caption font-normal normal-case leading-relaxed tracking-normal text-text-muted">
            Record the expected annual load factor to tighten water estimates. Blank uses the disclosed 50–100% screening default.
          </span>
        </fieldset>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          Operating model
          <select aria-label="Data center operating model" value={draft.operatingModel} onChange={(event) => setDraft(current => ({ ...current, operatingModel: event.target.value as FormDraft['operatingModel'] }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary">
            <option value={SITE_DEFAULT}>From Intent — {displayLabel(activeScenario.operatingModel)}</option>
            <option value="unknown">Unknown</option><option value="hyperscale">Hyperscale</option><option value="colocation">Colocation</option><option value="enterprise">Enterprise</option><option value="shell-lease">Shell lease</option>
          </select>
        </label>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          Workload
          <select aria-label="Data center workload" value={draft.workload} onChange={(event) => setDraft(current => ({ ...current, workload: event.target.value as FormDraft['workload'] }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary">
            <option value={SITE_DEFAULT}>From Intent — {displayLabel(activeScenario.workload)}</option>
            <option value="unknown">Unknown</option><option value="general-cloud">General cloud</option><option value="ai-hpc">AI / HPC</option><option value="inference">Inference</option><option value="mixed">Mixed</option>
          </select>
        </label>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          Construction duration (optional)
          <input type="number" min="1" step="1" aria-label="Construction duration months" value={draft.duration} onChange={(event) => setDraft(current => ({ ...current, duration: event.target.value, durationSource: 'edited' }))} placeholder="Using financial assumptions" className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" />
        </label>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          Heat rejection
          <select aria-label="Economic heat rejection" value={draft.heatRejection} onChange={(event) => setDraft(current => ({ ...current, heatRejection: event.target.value as FormDraft['heatRejection'] }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary">
            <option value={SITE_DEFAULT}>From Intent — {HEAT_REJECTION_LABELS[effectiveHeatRejection]}</option>
            {(Object.entries(HEAT_REJECTION_LABELS) as Array<[BopHeatRejection, string]>).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">
          IT cooling loop
          <select aria-label="Economic rack cooling strategy" value={draft.rackCoolingStrategy} onChange={(event) => setDraft(current => ({ ...current, rackCoolingStrategy: event.target.value as FormDraft['rackCoolingStrategy'] }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary">
            <option value={SITE_DEFAULT}>From Intent — {displayLabel(effectiveRackCooling)}</option>
            <option value="air">Air</option><option value="liquid">Liquid</option><option value="hybrid">Hybrid</option><option value="undecided">Undecided</option>
          </select>
        </label>
      </div>
      <div className="border-t border-border-subtle px-3 py-3">
        <label className="flex items-center gap-2 text-xs font-semibold text-text-primary">
          <input type="checkbox" checked={draft.fiscalEnabled} onChange={(event) => setDraft(current => ({ ...current, fiscalEnabled: event.target.checked }))} />
          Add an illustrative manual tax / PILOT scenario
        </label>
        <p className="mt-1 max-w-3xl type-caption leading-relaxed text-text-muted">
          Use known local assumptions where available. This is arithmetic screening, not a tax, incentive, or eligibility conclusion.
        </p>
        {draft.fiscalEnabled ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="fiscal-scenario-fields">
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">Jurisdiction<input ref={jurisdictionInputRef} aria-label="Fiscal jurisdiction" value={draft.jurisdiction} onChange={(event) => setDraft(current => ({ ...current, jurisdiction: event.target.value }))} placeholder="County, parish, city, or district" className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" /></label>
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">Assessed value (USD)<input type="number" min="0" step="1" aria-label="Fiscal assessed value" value={draft.assessedValue} onChange={(event) => setDraft(current => ({ ...current, assessedValue: event.target.value }))} placeholder="Taxable assessed value" className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" /></label>
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">Effective annual tax rate (%)<input type="number" min="0.0001" max="100" step="0.0001" aria-label="Fiscal annual tax rate percent" value={draft.taxRatePct} onChange={(event) => setDraft(current => ({ ...current, taxRatePct: event.target.value }))} placeholder="Combined effective rate" className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" /></label>
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">Term (years)<input type="number" min="1" max="100" step="1" aria-label="Fiscal scenario years" value={draft.fiscalYears} onChange={(event) => setDraft(current => ({ ...current, fiscalYears: event.target.value }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" /></label>
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">Annual abatement (%)<input type="number" min="0" max="100" step="0.1" aria-label="Fiscal annual abatement percent" value={draft.abatementPct} onChange={(event) => setDraft(current => ({ ...current, abatementPct: event.target.value }))} placeholder="0" className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" /></label>
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">Annual PILOT payment (USD)<input type="number" min="0" step="1" aria-label="Fiscal annual PILOT" value={draft.annualPilot} onChange={(event) => setDraft(current => ({ ...current, annualPilot: event.target.value }))} placeholder="0 when none is defined" className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary" /></label>
            <label className="type-caption font-semibold uppercase tracking-[0.09em] text-text-muted">PILOT treatment<select aria-label="Fiscal PILOT treatment" value={draft.pilotTreatment} onChange={(event) => setDraft(current => ({ ...current, pilotTreatment: event.target.value as FormDraft['pilotTreatment'] }))} className="mt-1 w-full border border-border-subtle bg-surface px-2 py-1.5 text-xs text-text-primary"><option value="replacement">Replaces tax after abatement</option><option value="additional">Adds to tax after abatement</option></select></label>
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-3 py-2.5">
        <p className="flex items-center gap-1.5 type-caption text-text-muted"><ShieldCheck className="h-3.5 w-3.5" aria-hidden />Intent and verified location data are reused first. No tax or incentive eligibility is inferred.</p>
        <Button type="button" size="sm" onClick={() => void run()} disabled={running}>
          {running ? <Loader2 className="animate-spin" aria-hidden /> : <Calculator aria-hidden />}
          {running ? 'Calculating…' : draft.fiscalEnabled ? 'Calculate screening estimate' : 'Calculate screening'}
        </Button>
        {error ? <p role="alert" className="basis-full text-xs text-danger">{error}</p> : null}
      </div>
    </details>
  );
});

EconomicScreeningControls.displayName = 'EconomicScreeningControls';
